import type { MapBrowserEvent } from 'ol';
import { Feature } from 'ol';
import {
  platformModifierKeyOnly,
  primaryAction,
  shiftKeyOnly,
} from 'ol/events/condition.js';
import type { Geometry } from 'ol/geom';
import { Point } from 'ol/geom';
import { Draw, Select } from 'ol/interaction';
import { Translate } from 'ol/interaction.js';
import type { DrawEvent } from 'ol/interaction/Draw';
import type { SelectEvent } from 'ol/interaction/Select';
import type { TranslateEvent } from 'ol/interaction/Translate';
import { toRaw } from 'vue';

import { DrawToolType } from '@nora-monorepo/nora-api-client';
import type { NamedLatLonCoordinate } from '@nora-monorepo/shared';
import {
  t,
  useConfirmModalStore,
} from '@nora-monorepo/ui-shared';

import { CreateFeatureBaseDrawTool } from '#nora-ui/map/drawtools/create-feature-base-draw-tool';
import {
  namedLatLonCoordinateToOlCoordinate,
  olToNamedLatLonCoordinate,
} from '#nora-ui/map/drawtools/draw-tool-utils';
import useRouteToolStore from '#nora-ui/map/drawtools/stores/route-tool-store';
import { getRoutePointStyle } from '#nora-ui/map/drawtools/styles/route-style';
import { getFeatureStyle } from '#nora-ui/map/drawtools/styles/styles';
import type { DrawToolOptions } from '#nora-ui/map/drawtools/types';
import { extractDrawToolOptions } from '#nora-ui/map/drawtools/types';
import { isFeature } from '#nora-ui/map/feature-properties';
import type { DrawToolManager } from '#nora-ui/map/map/DrawToolManager';
import { useGeofenceStore } from '#nora-ui/stores/geofence-store';

export class RouteTool extends CreateFeatureBaseDrawTool {
  drawToolType: DrawToolType = DrawToolType.route;
  options: DrawToolOptions = {
    drawToolType: this.drawToolType,
  };

  private readonly drawInteraction: Draw;
  private readonly selectDeleteInteraction: Select;
  private readonly translateInteraction: Translate;

  routeToolStore = useRouteToolStore(this.drawToolManager.map.getId());
  geofenceStore = useGeofenceStore();

  private readonly pointerMoveHandler: (
    event: MapBrowserEvent,
  ) => void;

  constructor(drawToolManager: DrawToolManager) {
    super(drawToolManager);
    this.drawToolName = 'Route';

    this.translateInteraction = new Translate({
      layers: [this.editingVectorLayer],
      hitTolerance: 10,
      filter: (feature) => {
        return feature.get('routePoint') === true;
      },
    });

    this.drawInteraction = new Draw({
      source: this.editingVectorSource,
      type: 'Point',
      style: getFeatureStyle,
      condition: (mapBrowserEvent) => {
        const map = mapBrowserEvent.map;
        const pixel = mapBrowserEvent.pixel;

        if (
          !primaryAction(mapBrowserEvent)
          || shiftKeyOnly(mapBrowserEvent)
          || platformModifierKeyOnly(mapBrowserEvent)
        ) {
          return false;
        }

        let isOverPoint = false;
        map.forEachFeatureAtPixel(
          pixel,
          (feature) => {
            if (feature.get('routePoint') === true) {
              isOverPoint = true;
              return true;
            }
            return false;
          },
          {
            hitTolerance: 10,
            layerFilter: layer => layer === this.editingVectorLayer,
          },
        );

        return !isOverPoint;
      },
    });

    this.selectDeleteInteraction = new Select({
      layers: [this.editingVectorLayer],
      condition(mapBrowserEvent) {
        return primaryAction(mapBrowserEvent) && shiftKeyOnly(mapBrowserEvent);
      },
      style: null,
      hitTolerance: 10,
    });

    this.drawInteraction.on('drawstart', this.onDrawStart.bind(this));
    this.drawInteraction.on('drawend', this.onDrawEnd.bind(this));
    this.selectDeleteInteraction.on('select', this.onDeletePoint.bind(this));
    this.translateInteraction.on(
      'translateend',
      this.onTranslateEnd.bind(this),
    );
    this.handleCloseTool = this.handleCloseTool.bind(this);

    this.pointerMoveHandler = this.handlePointerMove.bind(this);
  }

  override activate(): void {
    super.activate();
    const map = this.drawToolManager.map.getOlMapOrThrow();
    map.addInteraction(this.translateInteraction);
    map.addInteraction(this.selectDeleteInteraction);
    map.addInteraction(this.drawInteraction);
    map.on('pointermove', this.pointerMoveHandler);
  }

  override deactivate(): void {
    super.deactivate();
    if (this.routeToolStore.hoveredPointFeature) {
      this.routeToolStore.hoveredPointFeature = null;
    }
    const map = this.drawToolManager.map.getOlMapOrThrow();
    map.un('pointermove', this.pointerMoveHandler);
    map.removeInteraction(this.drawInteraction);
    map.removeInteraction(this.selectDeleteInteraction);
    map.removeInteraction(this.translateInteraction);
  }

  private handlePointerMove(evt: MapBrowserEvent): void {
    const map = this.drawToolManager.map.getOlMapOrThrow();
    const pixel = evt.pixel;

    if (isFeature(this.routeToolStore.hoveredPointFeature)) {
      this.routeToolStore.updatePointStyle(
        this.routeToolStore.hoveredPointFeature,
        'default',
      );
      this.routeToolStore.hoveredPointFeature = null;
    }

    // Check for cursor over route
    map.forEachFeatureAtPixel(
      pixel,
      (feature) => {
        if (feature.get('routePoint') === true) {
          const pointFeature = feature;
          if (isFeature(pointFeature)) {
            this.routeToolStore.hoveredPointFeature = pointFeature;
            this.routeToolStore.updatePointStyle(pointFeature, 'hover');
          }
          return true;
        }
        return false;
      },
      {
        hitTolerance: 10,
        layerFilter: layer => layer === this.editingVectorLayer,
      },
    );

    map.getViewport().style.cursor = this.routeToolStore.hoveredPointFeature === null
      ? 'default'
      : 'pointer';
  }

  override loadFeature(feature: Feature<Geometry>): boolean {
    const featureProperties = extractDrawToolOptions(feature);
    this.drawToolManager.updateDrawStyleOptions(featureProperties);

    const success = this.routeToolStore.loadFeature(
      feature,
      this.editingVectorSource,
      this.getDefaultProperties.bind(this),
    );
    if (success && isFeature(this.routeToolStore.routeFeature)) {
      this.feature = this.routeToolStore.routeFeature;
    }
    return success;
  }

  private async onTranslateEnd(event: TranslateEvent): Promise<void> {
    const features = event.features.getArray();

    for (const feature of features) {
      if (feature.get('routePoint') === true) {
        const featureIndex = feature.get('pointIndex') as number | undefined;

        if (featureIndex !== undefined) {
          const geometry = feature.getGeometry();
          if (geometry instanceof Point) {
            const olCoordinates = geometry.getCoordinates();
            this.routeToolStore.movePoint(featureIndex, olCoordinates);
          }
        }
      }
    }

    await this.handleRouteChange();
  }

  onDrawStart(event: DrawEvent): void {
    const olCoordinate = (
      event.feature.getGeometry() as Point
    ).getCoordinates();
    this.addPoint(olToNamedLatLonCoordinate(olCoordinate));
  }

  addPoint(namedLatLonCoordinate: NamedLatLonCoordinate): void {
    this.routeToolStore.addPoint(namedLatLonCoordinate);
    const pointFeature = new Feature({
      geometry: new Point(
        namedLatLonCoordinateToOlCoordinate(namedLatLonCoordinate),
      ),
    });

    const pointText = `${this.routeToolStore.routeCoordinates.length}`;
    pointFeature.set('routePoint', true);
    pointFeature.set('pointIndex', this.routeToolStore.pointFeatures.length);
    pointFeature.setStyle(getRoutePointStyle(pointText));
    this.routeToolStore.pointFeatures.push(pointFeature);
    this.editingVectorSource.addFeature(pointFeature);
  }

  async onDrawEnd(event: DrawEvent): Promise<void> {
    this.drawToolManager.addToUndoStack({
      type: 'add',
      features: [event.feature],
    });

    await this.handleRouteChange();
  }

  private async onDeletePoint(event: SelectEvent): Promise<void> {
    if (event.selected.length === 0) {
      return;
    }

    const selectedFeature = event.selected[0];
    const featureIndex
      = this.routeToolStore.pointFeatures.indexOf(selectedFeature);
    if (featureIndex !== -1) {
      await this.deletePoint(featureIndex);
    }

    this.selectDeleteInteraction.getFeatures().clear();
  }

  async handleRouteChange(): Promise<void> {
    await this.routeToolStore.updateRoute(
      this.editingVectorSource,
      this.getDefaultProperties.bind(this),
    );
  }

  async deletePoint(index: number): Promise<void> {
    this.routeToolStore.deletePoint(index, this.editingVectorSource);
    await this.handleRouteChange();
  }

  onCloseButtonPressed = (): void => {
    if (this.routeToolStore.pointFeatures.length === 0) {
      this.handleCloseTool();
      return;
    }
    const confirmModalStore = useConfirmModalStore();
    confirmModalStore.showConfirmModal({
      headerText: t('drawTools.route.closeTitle'),
      bodyText: t('drawTools.route.closeDescription'),
      confirmButtonLabel: t('common.discard'),
      onConfirm: this.handleCloseTool,
    });
  };

  handleCloseTool = (): void => {
    this.dismiss();
    this.geofenceStore.isGeofenceFeatureSelectionActive = false;
  };

  onSaveButtonPressed = async (): Promise<void> => {
    await this.save();
    this.dismiss();
    this.geofenceStore.isGeofenceFeatureSelectionActive = false;
  };

  override async save(): Promise<void> {
    if (!this.routeToolStore.routeFeature) {
      return;
    }

    if (
      this.routeToolStore.routeCoordinates.length > 0
      && isFeature(this.routeToolStore.routeFeature)
    ) {
      this.routeToolStore.routeFeature.setProperties({
        ...this.getDefaultProperties(),
        coordinates: [...toRaw(this.routeToolStore.routeCoordinates)],
        id: this.routeToolStore.featureId,
      });
      this.feature = toRaw(this.routeToolStore.routeFeature);
    }

    await super.save();
  }

  override onStylingUpdated(): void {
    super.onStylingUpdated();
    if (this.routeToolStore.routeFeature) {
      const styleProperties = this.getDefaultProperties();
      this.routeToolStore.routeFeature.setProperties({
        ...styleProperties,
        coordinates: this.routeToolStore.routeCoordinates,
      });
      this.routeToolStore.routeFeature.setStyle(getFeatureStyle);
    }
  }

  override dismiss() {
    super.dismiss();
    this.routeToolStore.reset();
  }
}
